import { createContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFetch } from 'src/consumer/services'
import { ClinicDataProvider } from 'src/context/ClinicDataContext'
import { LayoutContainer } from 'src/mentor/layout/Styles'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import ClinicLayoutProvider from '../context/ClinicLayoutProvider'
import { getData } from 'src/consumer/services/profileService'
import { getCPDataWithoutParams } from '../services'
import { ROLE_TYPES } from 'src/consumer/constants'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { ClinicSetupContext, LayoutChildComponent } from '.'
import { getDataWithoutParams } from 'src/consumer/services/featureServices'

const availableRoles = getAllRolesInBoolean()

export default function ClinicOwnerLayout() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [userProfileData, setUserProfileData] = useState()
  const [associatedClinicsData, setAssociatedClinicsData] = useState([])
  const [associatedClinicChambersData, setAssociatedClinicChambersData] = useState([])
  const [associatedClinicBrandsData, setAssociatedClinicBrandsData] = useState([])
  const [ownerBrandPeopleUserSummary, setOwnerBrandPeopleUserSummary] = useState()
  const [ownerBrandMentorsData, setOwnerBrandMentorsData] = useState()

  async function getAndSetOwnerClinicAssociations() {
    const respTWO = await getData('/api/secure/cp/clinic-profile')
    if (respTWO?.status === 200) {
      setAssociatedClinicsData(respTWO?.data || [])
    }
  }

  async function getAndSetOwnerClinicBrandAssociations() {
    const respTHREE = await getData('/api/secure/cp/clinic-brand')
    if (respTHREE?.status === 200) {
      setAssociatedClinicBrandsData(respTHREE?.data || [])
      Promise.all([
        await getAndSetOwnerBrandPeopleUserSummary(respTHREE?.data),
        await getAndSetOwnerBrandMentorsData(respTHREE?.data),
        await getAndSetOwnerClinicChambersData(respTHREE?.data),
      ])
    }
  }

  async function getAndSetOwnerBrandPeopleUserSummary(associatedBrands) {
    const promises = associatedBrands?.map(async (brand) => {
      const respFOUR = await getDataWithoutParams(
        `/api/secure/cp/clinic-brand/people/get-user-summary?clinicBrandId=${brand?.id}`,
      )
      if (respFOUR?.status === 200) {
        return { [brand.id]: respFOUR?.data }
      }
      return null
    })
    const results = await Promise.all(promises)
    const objToSet = {}
    results.forEach((result) => {
      if (result) {
        const brandId = Object.keys(result)[0]
        objToSet[brandId] = result[brandId]
      }
    })
    setOwnerBrandPeopleUserSummary(objToSet)
  }

  async function getAndSetOwnerBrandMentorsData(associatedBrands) {
    const promises = associatedBrands?.map(async (brand) => {
      const respFOUR = await getDataWithoutParams(
        `/api/secure/cp/clinic-brand/people/get-attached-mentors-data?clinicBrandId=${brand?.id}`,
      )
      if (respFOUR?.status === 200) {
        return { [brand.id]: respFOUR?.data }
      }
      return null
    })
    const results = await Promise.all(promises)
    const objToSet = {}
    results.forEach((result) => {
      if (result) {
        const brandId = Object.keys(result)[0]
        objToSet[brandId] = result[brandId]
      }
    })
    setOwnerBrandMentorsData(objToSet)
  }

  async function getAndSetOwnerClinicChambersData(associatedBrands) {
    const promises = associatedBrands?.map(async (brand) => {
      const respFOUR = await getDataWithoutParams(
        `/api/secure/cp/clinic-chamber/list?id=${brand?.id}&type=clinic-brand`,
      )
      // or id=clinicLocationId and type=clinic
      if (respFOUR?.status === 200) {
        return { [brand.id]: respFOUR?.data }
      }
      return null
    })
    const results = await Promise.all(promises)
    const objToSet = {}
    results.forEach((result) => {
      if (result) {
        const brandId = Object.keys(result)[0]
        objToSet[brandId] = result[brandId]
      }
    })
    setAssociatedClinicChambersData(objToSet)
  }

  async function setDataForClinicOwner() {
    if (
      availableRoles?.isClinicOwner ||
      availableRoles?.isClinicProspect ||
      availableRoles?.isClinicManager
    ) {
      setLoading(true)
      try {
        const respONE = await getData('/api/secure/user/profile')
        if (respONE?.status === 200) {
          setUserProfileData(respONE.data)
          localStorage.setItem('authorities', JSON.stringify(respONE?.data?.authorities))
        }
      } catch (error) {
        // Handle any errors from the data fetching here
      }
      await getAndSetOwnerClinicAssociations()
      await getAndSetOwnerClinicBrandAssociations()
      setLoading(false)
    } else {
      navigate('/aumhum-for-clinics')
    }
  }

  useEffect(() => {
    setDataForClinicOwner()
  }, [])

  if (loading) {
    return <LoadingSpinner bgColor="#F5F5F599" dark />
  } else {
    return (
      <LayoutContainer>
        <ClinicLayoutProvider>
          <ClinicDataProvider
            chosenRoleFlow={ROLE_TYPES.roleClinicOwner}
            ownerData={{
              userProfileData,
              associatedClinicsData,
              associatedClinicChambersData,
              associatedClinicBrandsData,
              ownerBrandPeopleUserSummary,
              ownerBrandMentorsData,
            }}
            refreshOwnerAssociatedClinicsData={getAndSetOwnerClinicAssociations}
            refreshOwnerAssociatedClinicBrandsData={getAndSetOwnerClinicBrandAssociations}
            refreshOwnerAssociatedClinicChambersData={() =>
              getAndSetOwnerClinicChambersData(associatedClinicBrandsData)
            }
          >
            <LayoutChildComponent />
          </ClinicDataProvider>
        </ClinicLayoutProvider>
      </LayoutContainer>
    )
  }
}

// async function getAndSetLegalPolicies() {
//   const allLegalPolicies = await getLegalPolicies()
//   if (allLegalPolicies) {
//     setAllLegalPolicies(allLegalPolicies)
//   }
// }
